import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { MarketingLayout } from "@/components/MarketingLayout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { SUBSCRIPTION_TIERS } from "@/lib/subscriptionTiers";

type Result = {
  ok: true;
  brandId: string;
  inviteUrl?: string;
};

export default function StartTrial() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [brandName, setBrandName] = useState("");
  const [storefrontUrl, setStorefrontUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const selectedTier = SUBSCRIPTION_TIERS.find(
    (tier) => tier.id === params.get("plan")
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const res = await api<Result>("/customer/start-trial", {
      method: "POST",
      body: JSON.stringify({
        email: email.trim(),
        brandName: brandName.trim(),
        storefrontUrl: storefrontUrl.trim(),
      }),
    });
    setSubmitting(false);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Couldn't start your trial.");
      return;
    }
    // Bounce straight to WorkOS to set up sign-in for the trial account.
    if (res.data.inviteUrl) {
      window.location.href = res.data.inviteUrl;
      return;
    }
    setResult(res.data);
  }

  return (
    <MarketingLayout>
      <div className="mx-auto w-full max-w-2xl px-6 py-16">
        <div className="text-center">
          <div className="text-sm font-mono uppercase tracking-[0.2em] text-emerald-700">
            Free 30-day trial
          </div>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight text-slate-900 md:text-5xl">
            Start monitoring your catalog
          </h1>
          <p className="mt-4 text-base text-slate-600">
            We'll scan your storefront, start monitoring reviews, flag Amazon
            policy issues, and prepare appeal-ready submissions.
          </p>
          {selectedTier && (
            <p className="mt-3 text-sm font-medium text-emerald-700">
              {`Selected plan: ${selectedTier.name} · $${selectedTier.priceMonthly}/mo · ${selectedTier.quota}`}
            </p>
          )}
        </div>

        {!result && (
          <form onSubmit={onSubmit} className="mt-10 space-y-5">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Your email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@brandname.com"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Brand name
              </label>
              <input
                type="text"
                required
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Your brand on Amazon"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Amazon storefront URL
              </label>
              <input
                type="url"
                required
                value={storefrontUrl}
                onChange={(e) => setStorefrontUrl(e.target.value)}
                placeholder="https://www.amazon.com/stores/YourBrand/page/…"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              />
              <p className="mt-1 text-xs text-slate-500">
                We'll find your products automatically. Ongoing plan limits are
                confirmed before billing starts.
              </p>
            </div>

            {error && (
              <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
                <AlertTriangle className="h-4 w-4" /> {error}
              </p>
            )}

            <Button type="submit" size="lg" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "Setting up…" : "Start free trial →"}
            </Button>
            <p className="text-xs text-slate-500">
              No credit card. Trial converts to a paid plan only if you choose to.
            </p>
          </form>
        )}

        {result && (
          <div className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 md:p-8">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              <h2 className="text-2xl font-semibold tracking-tight text-emerald-900">
                Trial started — check your email
              </h2>
            </div>
            <p className="mt-3 text-sm text-emerald-900">
              We sent a sign-in link to <strong>{email}</strong>. We're scanning your
              storefront for products now — they'll be ready in your dashboard
              shortly. Click the email to sign in.
            </p>
            <div className="mt-5">
              <Link
                to="/login"
                className="inline-flex rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                Sign in →
              </Link>
            </div>
          </div>
        )}
      </div>
    </MarketingLayout>
  );
}
